import { useDonationContextProvider } from 'context/DonationContextProvider';
import { useEffect, useState } from 'react';
import { Button, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faEnvelope, faShoppingBag } from '@fortawesome/free-solid-svg-icons';
import { faTwitter, faFacebook, faYoutube, faInstagram, faTiktok } from '@fortawesome/free-brands-svg-icons';

import { DonationHeader } from './DonationHeader';
import { DonationFooter } from './DonationFooter';
import shopImage from 'assets/img/we-raise-images/front-end/shop-dates.jpeg';

function DonationThanksPage() {
  const { setBasket, setDonor, setClientSecret, setStripeKey } = useDonationContextProvider();
  const [paymentStatus, setPaymentStatus] = useState('processing');

  useEffect(() => {
    // Check URL params for Stripe payment status
    const urlParams = new URLSearchParams(window.location.search);
    const redirectStatus = urlParams.get('redirect_status');

    if (redirectStatus === 'succeeded') {
      setPaymentStatus('succeeded');
    } else if (redirectStatus === 'processing') {
      setPaymentStatus('processing');
    } else if (redirectStatus) {
      setPaymentStatus('failed');
    }

    // Clear all donation state - order is created by webhook
    setBasket([]);
    setDonor({});
    setClientSecret('');
    setStripeKey('');
  }, [setBasket, setDonor, setClientSecret, setStripeKey]);

  return (
    <div className="bg-wr-donation-grey-50">
      <DonationHeader />
      <div className="container py-6">
        <div className="mx-auto mb-2 rounded-5 d-flex flex-column gap-25 wr-donation-thanks-card">
          {/* Video Section */}
          <div className="position-relative">
            <div className="ratio ratio-16x9">
              <iframe
                src="https://player.vimeo.com/video/207283207?h=71a69a4a2c"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Thank You Video"
                className="rounded"
              />
            </div>
          </div>

          {/* Thank You Header with Heart Icon */}
          <div className="text-center py-4">
            <div
              className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #facc15 0%, #f59e0b 100%)',
                boxShadow: '0 4px 15px rgba(250, 204, 21, 0.4)'
              }}
            >
              <FontAwesomeIcon icon={faHeart} className="text-white" style={{ fontSize: '36px' }} />
            </div>
            <h1
              className="font-patrick mb-2 wr-donation-text-primary"
              style={{ fontSize: '48px', fontWeight: 'bold' }}
            >
              A Huge Thank You!
            </h1>
            <p
              className="mb-3"
              style={{ fontSize: '32px', color: '#d97706' }}
            >
              Jazāk Allāhu Khayran
            </p>
            <p className="fs-0 wr-donation-text-grey-700 mb-0 px-md-5" style={{ fontSize: '18px', lineHeight: '1.7' }}>
              We are ever so grateful for your generous contribution. Your
              support truly means the world to us. Because of you, we can
              continue our work and make a meaningful difference in the lives of
              those we serve.
            </p>
          </div>

          {/* Info Cards Row */}
          <Row className="g-4 mb-4">
            {/* Receipt Card */}
            <Col md={6}>
              <div
                className="h-100 p-4 rounded-4 text-center"
                style={{
                  background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                  border: '1px solid #bfdbfe'
                }}
              >
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{ width: '50px', height: '50px', backgroundColor: '#3b82f6' }}
                >
                  <FontAwesomeIcon icon={faEnvelope} className="text-white" style={{ fontSize: '20px' }} />
                </div>
                <h5 className="fw-bold mb-2" style={{ color: '#1e40af' }}>Receipt Sent</h5>
                <p className="mb-0 text-muted" style={{ fontSize: '14px' }}>
                  A receipt for your donation has been sent to your registered email address.
                </p>
              </div>
            </Col>

            {/* Shop Card - Diagonal Split */}
            <Col md={6}>
              <a
                href="https://shop.helpyateem.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-decoration-none"
              >
                <div
                  className="h-100 rounded-4 position-relative overflow-hidden"
                  style={{
                    minHeight: '180px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(250, 204, 21, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Orange diagonal background */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      clipPath: 'polygon(0 0, 60% 0, 40% 100%, 0 100%)',
                      zIndex: 1
                    }}
                  />
                  {/* Image diagonal background */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url(${shopImage})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      clipPath: 'polygon(60% 0, 100% 0, 100% 100%, 40% 100%)',
                      zIndex: 1
                    }}
                  />
                  {/* Content overlay */}
                  <div
                    className="position-relative d-flex flex-column justify-content-center h-100 p-4"
                    style={{ zIndex: 2 }}
                  >
                    <div className="d-flex align-items-center">
                      <div>
                        <div
                          className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
                          style={{ width: '45px', height: '45px', backgroundColor: 'rgba(255,255,255,0.9)' }}
                        >
                          <FontAwesomeIcon icon={faShoppingBag} style={{ fontSize: '20px', color: '#d97706' }} />
                        </div>
                        <h5 className="fw-bold mb-1 text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.2)' }}>
                          Visit Our Shop
                        </h5>
                        <p className="mb-0 text-white" style={{ fontSize: '13px', opacity: 0.9 }}>
                          shop.helpyateem.org
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </a>
            </Col>
          </Row>

          {/* Social Media Section */}
          <div
            className="text-center p-4 rounded-4 mb-4"
            style={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              border: '2px solid #e2e8f0'
            }}
          >
            <h4 className="fw-bold mb-2" style={{ color: '#334155' }}>Stay Connected</h4>
            <p className="text-muted mb-3" style={{ fontSize: '15px' }}>Follow us on social media for updates</p>
            <p className="fw-bold mb-3" style={{ color: '#3b82f6', fontSize: '18px' }}>@helpyateem</p>
            <div className="d-flex justify-content-center gap-3">
              <a
                href="https://twitter.com/helpyateem"
                aria-label="Follow us on X/Twitter"
                target="_blank"
                rel="noopener noreferrer"
                className="d-flex align-items-center justify-content-center rounded-circle text-white"
                style={{
                  width: '50px',
                  height: '50px',
                  backgroundColor: '#1da1f2',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <FontAwesomeIcon icon={faTwitter} style={{ fontSize: '22px' }} />
              </a>
              <a
                href="https://www.facebook.com/helpyateem"
                aria-label="Follow us on Facebook"
                target="_blank"
                rel="noopener noreferrer"
                className="d-flex align-items-center justify-content-center rounded-circle text-white"
                style={{
                  width: '50px',
                  height: '50px',
                  backgroundColor: '#1877f2',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <FontAwesomeIcon icon={faFacebook} style={{ fontSize: '22px' }} />
              </a>
              <a
                href="https://www.instagram.com/helpyateem"
                aria-label="Follow us on Instagram"
                target="_blank"
                rel="noopener noreferrer"
                className="d-flex align-items-center justify-content-center rounded-circle text-white"
                style={{
                  width: '50px',
                  height: '50px',
                  background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <FontAwesomeIcon icon={faInstagram} style={{ fontSize: '22px' }} />
              </a>
              <a
                href="https://www.youtube.com/channel/UCBr7aFY8fQg8fwKB9KK3YfA"
                aria-label="Visit our YouTube channel"
                target="_blank"
                rel="noopener noreferrer"
                className="d-flex align-items-center justify-content-center rounded-circle text-white"
                style={{
                  width: '50px',
                  height: '50px',
                  backgroundColor: '#ff0000',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <FontAwesomeIcon icon={faYoutube} style={{ fontSize: '22px' }} />
              </a>
              <a
                href="https://www.tiktok.com/@helpyateem"
                aria-label="Follow us on TikTok"
                target="_blank"
                rel="noopener noreferrer"
                className="d-flex align-items-center justify-content-center rounded-circle text-white"
                style={{
                  width: '50px',
                  height: '50px',
                  backgroundColor: '#000000',
                  transition: 'transform 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                <FontAwesomeIcon icon={faTiktok} style={{ fontSize: '22px' }} />
              </a>
            </div>
          </div>

          {/* Gratitude Section */}
          <div className="text-center mb-4">
            <p className="fs-5 wr-donation-text-grey-700 mb-1" style={{ fontStyle: 'italic' }}>
              With deep gratitude,
            </p>
            <p className="fs-4 fw-bold wr-donation-text-primary mb-0">
              The Help Yateem Team
            </p>
          </div>

          {/* Feedback Button */}
          <div className="d-flex flex-column">
            <Button
              href="https://helpyateem.org/contact-us/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-100 py-3 fw-bold text-uppercase"
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                border: 'none',
                fontSize: '16px',
                letterSpacing: '0.5px',
                borderRadius: '12px',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Help Us By Giving Your Feedback
            </Button>
          </div>
        </div>
      </div>
      <DonationFooter />
    </div>
  );
}

export { DonationThanksPage };
